import moment from 'moment';
import { phoneFormatter } from '../../../utils/redux-form-helper';
import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';
import { PROSPECT } from '../constants';

// The Activity Summary only counts activities saved as Prospect or Applicant, so an activity
// logged from Manage Prospects has to carry its customer status, as the V1 screen's action does.
// A status the activity already has (Applicant, Current Resident) is kept.
export const buildProspectActivity = (activity) => ({
  ...activity,
  customerStatus: activity.customerStatus || PROSPECT,
});

// Parse comma-delimited strings into trimmed values.
// Example: "Studio, 1 Bedroom" -> ["Studio", "1 Bedroom"]
export const parseCommaDelimited = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

/**
 * Extracts and formats all lead sources for a prospect in chronological order
 * @param {Object} prospect - The prospect data object
 * @param {Object} prospect.contact - The contact object containing leadSources
 * @param {Array} prospect.contact.leadSources - Array of lead source objects with createdAt timestamps
 * @param {Object} prospect.referralType - Fallback referral type object
 * @returns {string} Comma-separated string of lead source names or 'N/A'
 */
export function extractLeadSourcesString(prospect) {
  const allLeadSourceNames = [];

  // Sort lead sources chronologically by createdAt (earliest first)
  const leadSources = (prospect.contact?.leadSources || []).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  leadSources.forEach((leadSource) => {
    if (leadSource.referralType?.name) {
      allLeadSourceNames.push(leadSource.referralType.name);
    }
  });

  // If no lead sources from contact.leadSources, fallback to primary referralType
  if (allLeadSourceNames.length === 0 && prospect.referralType?.name) {
    allLeadSourceNames.push(prospect.referralType.name);
  }

  return allLeadSourceNames.length > 0 ? allLeadSourceNames.join(', ') : 'N/A';
}

/**
 * Extracts unique lead source names from prospects for filter options
 * @param {Array} prospects - Array of prospect objects
 * @returns {Set} Set of unique lead source names
 */
export function extractUniqueLeadSources(prospects) {
  const leadSourceOptions = new Set();

  prospects.forEach((prospect) => {
    // Extract from contact.leadSources (primary source of truth)
    if (prospect.contact?.leadSources) {
      prospect.contact.leadSources.forEach((leadSource) => {
        if (leadSource.referralType?.name) {
          leadSourceOptions.add(leadSource.referralType.name);
        }
      });
    }

    // Fallback to referralType if no contact.leadSources
    if (
      (!prospect.contact?.leadSources ||
        prospect.contact.leadSources.length === 0) &&
      prospect.referralType?.name
    ) {
      leadSourceOptions.add(prospect.referralType.name);
    }
  });

  return leadSourceOptions;
}

/**
 * Custom filter function for lead sources that handles comma-separated values
 * @param {string[]} filterByArr - Array of selected lead source names from filter
 * @param {string} filterValue - Comma-separated string of all lead sources for a prospect
 * @returns {boolean} True if any selected lead source is found in the prospect's lead sources
 */
export function leadSourceFilterFn(filterByArr, filterValue) {
  if (!filterByArr || filterByArr.length === 0) {
    return true; // No filter applied, show all
  }

  if (!filterValue || filterValue === 'N/A') {
    return false; // No lead sources to match against
  }

  const prospectLeadSources = parseCommaDelimited(filterValue).map((source) =>
    source.toLowerCase(),
  );

  // Note: filterByArr is already lowercase from the table component
  return filterByArr.some((selected) => prospectLeadSources.includes(selected));
}

export function parseCSV({
  sortedAndFilteredResults,
  filteredHeaders,
  showTextingColumn,
}) {
  const headers = filteredHeaders
    .filter(({ id }) => {
      const hiddenColumns = ['actions'];

      if (showTextingColumn === false) hiddenColumns.push('texting');

      return !hiddenColumns.includes(id);
    })
    .map(({ id, label }) => {
      return typeof label === 'string' ? label : label.props?.defaultMessage;
    })
    .join(',');
  const rows = sortedAndFilteredResults.map((prospect) => {
    const data = [];
    const createdAt = prospect.createdAt;
    const createdAtFormatted = createdAt
      ? `"${moment(createdAt).format('ll')}"`
      : '';
    const lastActivity = prospect?.lastActivity?.startTime ?? null;
    const lastActivityFormatted = lastActivity
      ? `"${moment(lastActivity).format('ll')}"`
      : '';
    const nextActivity = prospect?.nextActivity?.startTime ?? null;
    const nextActivityFormatted = nextActivity
      ? `"${moment(nextActivity).format('ll')}"`
      : '';
    const moveInDate = prospect?.prospectPreferences?.moveInDateFrom ?? null;
    const moveInDateFormatted = moveInDate
      ? `"${moment(moveInDate).format('ll')}"`
      : '';
    const nBeds = formatNBedsArr(
      prospect?.prospectPreferences?.nBedsArr,
      prospect?.prospectPreferences?.nBeds,
    );
    const assignedTo = `"${prospect?.assignedTo?.firstName ?? ''} ${
      prospect?.assignedTo?.lastName ?? ''
    }"`;
    const phoneNumber = prospect?.phoneNumber ?? '';
    const phoneNumberFormatted = `"${phoneFormatter(phoneNumber)}"`;
    const textingIndicatorsData = showTextingColumn
      ? [
          `"New = ${prospect.texting?.NEW ?? 0}, Unresolved = ${
            prospect.texting.UNRESOLVED ?? 0
          }"`,
        ]
      : [];

    if (headers.includes('Status'))
      data.push(prospect?.currentProspectStatus?.name ?? '');
    if (headers.includes('Prospect Name'))
      data.push(`"${prospect.prospectName}"`);
    if (headers.includes('Phone Number')) data.push(phoneNumberFormatted);
    if (headers.includes('Texting')) data.push(...textingIndicatorsData);
    if (headers.includes('Email')) data.push(phoneNumberFormatted);
    if (headers.includes('Prospect Created')) data.push(createdAtFormatted);
    if (headers.includes('Last Activity')) data.push(lastActivityFormatted);
    if (headers.includes('Next Activity')) data.push(nextActivityFormatted);
    if (headers.includes('Desired Move-In Date'))
      data.push(moveInDateFormatted);
    if (headers.includes('# of Bedrooms')) data.push(nBeds);
    if (headers.includes('Assigned To')) data.push(assignedTo);

    return data.join(',');
  });

  return { headers, rows };
}

/**
 * Filter ids whose saved selections are checked against the options built from
 * the loaded prospects. Each one reads its options from `${id}Options`.
 */
const VALIDATED_FILTER_IDS = ['nBeds', 'status', 'leadSource'];

/**
 * Ids of the saved filters that hold a selection the current options no longer
 * offer, so the caller can clear them instead of leaving the table empty with
 * no visible cause.
 *
 * Returns nothing until the prospects load. The option lists are empty until
 * then, so every saved selection would look invalid and the user would lose
 * their filters on each page load.
 */
export function getFiltersToClear({ prospects, filterState, filterOptions }) {
  if (!prospects?.length || !filterState) return [];

  return VALIDATED_FILTER_IDS.filter((id) => {
    const selection = filterState[id];

    if (!Array.isArray(selection)) return false;

    const validValues = filterOptions[`${id}Options`].map((opt) => opt.value);

    return selection.some((value) => !validValues.includes(value));
  });
}
